import { useCallback, useEffect, useState } from 'react';

interface WidthItem {
  id: number;
  width: number;
}

interface CurrentRow {
  items: WidthItem[];
  width: number;
}

const calculateWidths = (
  items: WidthItem[],
  rowWidth: number,
  customWidths: { [id: number]: number }): WidthItem[] => {

  if (!items && rowWidth !== 0) {
    return [];
  }

  let currentRow: CurrentRow = { items: [], width: 0 };
  let rows = [currentRow];

  for (const item of items) {
    const minWidth = (customWidths?.[item.id] ?? 0) > item.width
      ? customWidths[item.id]
      : item.width;

    if (currentRow.width + minWidth > rowWidth) {
      currentRow = { items: [], width: 0 };
      rows = [
        ...rows,
        currentRow
      ];
    }

    currentRow.items = [
      ...currentRow.items,
      {
        id: item.id,
        width: minWidth
      }
    ];
    currentRow.width += minWidth;
  }

  return rows.reduce((result, row) => {
    const remainingWidth = (rowWidth - row.width) /
      row.items.filter(item => item.width > 0).length;

    return [
      ...result,
      ...row.items.map(item => ({
        id: item.id,
        width: item.width > 0
          ? (item.width + remainingWidth) / rowWidth * 100
          : 0
      }))
    ];
  }, [] as WidthItem[]);
};

export const compareWidths = (
  currentWidths: WidthItem[],
  newWidths: WidthItem[]
): boolean => {
  if (currentWidths?.length !== newWidths?.length) {
    return true;
  }

  return (newWidths || []).some(
    newWidth => {
      const currentWidth = (currentWidths || []).find(
        width => width.id === newWidth.id);

      return !currentWidth || newWidth.width !== currentWidth.width;
    }
  );
};

export const useCustomWidths = (
  items: WidthItem[] | undefined,
  rowWidth: number
): {
  minWidths: WidthItem[];
  requestCustomWidth: (id: number, value: number) => void;
} => {

  const [customWidths, updateCustomWidths] = useState({});
  const [minWidths, updateMinWidths] = useState(() => calculateWidths(items, rowWidth, customWidths));

  useEffect(() => {
    const newWidths = calculateWidths(items, rowWidth, customWidths);
    if (compareWidths(minWidths, newWidths)) {
      updateMinWidths(newWidths);
    }
  }, [items, rowWidth, customWidths, updateMinWidths]);

  const requestCustomWidth = useCallback((id: number, value: number) => {
    if (customWidths[id] !== value) {
      updateCustomWidths({
        ...customWidths,
        [id]: value
      });
    }
  }, [customWidths, updateCustomWidths]);

  return { minWidths, requestCustomWidth };
};
